import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Hotel, BadgePercent, MessageCircle, Building, MapPin, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyOnboarding = () => {
  const navigate = useNavigate();
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [leadId, setLeadId] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [password, setPassword] = useState('');
  const [chatMessages, setChatMessages] = useState([
    { type: 'bot', text: 'Ciao! 👋 Benvenuto su traveldiscount.ai! Sono qui per aiutarti a registrare la tua struttura. Come si chiama la tua proprietà?' }
  ]);

  const addMessage = (type, text) => {
    setChatMessages(prev => [...prev, { type, text }]);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!userInput.trim() || loading) return;

    const message = userInput.trim();
    setUserInput('');
    addMessage('user', message);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat/onboarding`, {
        session_id: sessionId,
        message: message
      });

      addMessage('bot', response.data.response);

      if (response.data.complete && response.data.lead_id) {
        setLeadId(response.data.lead_id);
        setOnboardingComplete(true);
        setTimeout(() => {
          addMessage('bot', 'Ora crea una password per il tuo account (minimo 6 caratteri):');
        }, 1000);
      }
    } catch (error) {
      console.error('Chat error:', error);
      addMessage('bot', 'Mi dispiace, c\'è stato un errore. Potresti riprovare?');
      toast.error('Errore nella chat. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteOnboarding = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/onboarding/complete`, {
        lead_id: leadId,
        password: password
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      addMessage('bot', '🎉 Perfetto! Il tuo account è pronto.');
      toast.success('Account creato con successo!');
      
      setTimeout(() => {
        navigate('/owner/dashboard');
      }, 1500);
    } catch (error) {
      console.error('Onboarding completion error:', error);
      toast.error('Errore nel completare la registrazione.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Navbar */}
      <nav className="px-6 py-4 flex items-center justify-between glass-effect">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <div className="relative w-11 h-11 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md">
            <Hotel className="w-6 h-6 text-white" />
            <div className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-sm border-2 border-white">
              <BadgePercent className="w-3.5 h-3.5 text-white" />
            </div>
          </div>
          <h1 className="text-lg font-bold text-gray-900">traveldiscount.ai</h1>
        </div>
        <Button variant="outline" onClick={() => navigate('/owner/login')}>
          Already have an account?
        </Button>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Property Registration</h2>
          <p className="text-gray-600">Let's get your property listed on traveldiscount.ai</p>
        </div>

        {/* Chat Interface */}
        <Card className="p-6 mb-6 max-h-96 overflow-y-auto" data-testid="chat-container">
          <div className="space-y-4">
            {chatMessages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs px-4 py-3 rounded-2xl ${
                    msg.type === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                  data-testid={`chat-message-${index}`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Input Area */}
        <Card className="p-6">
          {!onboardingComplete ? (
            <form onSubmit={handleSendMessage}>
              <div className="flex gap-3">
                <Input
                  placeholder="Scrivi la tua risposta..."
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  data-testid="chat-input"
                  autoFocus
                  disabled={loading}
                  className="flex-1"
                />
                <Button 
                  type="submit" 
                  className="bg-blue-600 px-6" 
                  disabled={!userInput.trim() || loading}
                  data-testid="send-message-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    'Invia'
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleCompleteOnboarding}>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Crea Password
              </label>
              <Input
                type="password"
                placeholder="Minimo 6 caratteri"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="password-input"
                autoFocus
                minLength={6}
              />
              <Button type="submit" className="w-full mt-4 bg-blue-600" disabled={password.length < 6}>
                Completa Registrazione
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PropertyOnboarding;